import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestoreIcon from "@mui/icons-material/Restore";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { saveFile } from "../../../services/backend/EditorServices";
import { savePqrsdConfig } from "../../../services/backend/PqrsdConfigService";
import PqrsdTemplateDialog from "./PqrsdTemplateDialog";
import { buildMergedDocument } from "./mergeLetterhead";
import {
  ANSWER_TAGS,
  DOCUMENT_KINDS,
  MAX_ANSWER_TEMPLATES,
  MAX_TEMPLATE_BYTES,
  allDocKeys,
  answerKey,
  bodyOf,
  bodyPath,
  documentStatus,
  effectiveLetterheadId,
  findAnswer,
  isAnswerKey,
  kindsUsingLetterhead,
  letterheadPath,
  mergedOf,
  mergedPath,
  normalizeDocumentsConfig,
  withBody,
  withMerged,
} from "./documentTemplates";
import defaultFilingText from "./defaults/filing.docx";
import defaultInfoRequestText from "./defaults/infoRequest.docx";
import defaultDesistimientoText from "./defaults/desistimiento.docx";
import defaultAppealDecisionText from "./defaults/appealDecision.docx";
import defaultAnswerText from "./defaults/answer.docx";

import { pqrsdText } from "../pqrsdLabel";
// Membretes and the text of each document the module files by itself.
//
// Nobody edits a final document here. The admin writes each document's text
// (body only) and uploads membretes (header + footer, empty body); the browser
// pours each text into its membrete and stores the result under merged[kind],
// which is what the cloud functions render from. Replacing a membrete rebuilds
// every document that uses it, so a new membrete never means rewriting a text.

const STATUS = {
  ready: { label: "Listo", color: "success" },
  no_text: { label: "Sin texto", color: "default" },
  no_letterhead: { label: "Sin membrete", color: "warning" },
  stale: { label: "Desactualizado", color: "warning" },
};

// Starting texts, shipped with the app. Radicación is the body of the "Formato
// PQRSD" the filing used before this tab existed, with its
// membrete stripped and the unsent <<noSign>> marker removed; the two acts are
// written against the tags their backend sends. An admin who never touches a
// text still gets a correct document by adopting these.
const DEFAULT_TEXTS = {
  filing: defaultFilingText,
  infoRequest: defaultInfoRequestText,
  desistimiento: defaultDesistimientoText,
  appealDecision: defaultAppealDecisionText,
};

// Every answer template starts from the same letter: destinatario, the PQRSD
// it answers and a placeholder paragraph for the substance.
const defaultTextFor = (key) => (isAnswerKey(key) ? defaultAnswerText : DEFAULT_TEXTS[key]);

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Returns an error message for an unusable upload, or null.
const checkDocx = (file) => {
  if (!/\.docx$/i.test(file.name)) return "El archivo debe ser un documento Word (.docx).";
  if (file.size > MAX_TEMPLATE_BYTES) return "El archivo no puede superar 8 MB.";
  return null;
};

export default function PqrsdDocuments({ documents, onDocumentsChange, onFeedback }) {
  const docs = normalizeDocumentsConfig(documents);

  // Uploads, autosave callbacks and the rebuild queue all run async and outlive
  // renders; they read and write through this ref so none of them persists a
  // stale copy over a newer one.
  const docsRef = useRef(docs);
  useEffect(() => {
    docsRef.current = normalizeDocumentsConfig(documents);
  }, [documents]);

  const [busy, setBusy] = useState(null); // what is uploading: letterhead id, "new", or `body:${kind}`
  const [building, setBuilding] = useState({}); // { [kind]: true }
  const [buildErrors, setBuildErrors] = useState({}); // { [kind]: message }
  const [editorKind, setEditorKind] = useState(null);
  const editorSaved = useRef(false);
  const queue = useRef(Promise.resolve());

  const persist = async (update) => {
    const next = normalizeDocumentsConfig(update(docsRef.current));
    docsRef.current = next;
    onDocumentsChange(next);
    if (!(await savePqrsdConfig({ documents: next }))) {
      throw new Error("No fue posible guardar la configuración de documentos.");
    }
    return next;
  };

  const fail = (msg) => onFeedback({ severity: "error", msg });

  const buildOne = async (kind) => {
    const current = docsRef.current;
    const body = bodyOf(current, kind);
    const letterheadId = effectiveLetterheadId(current, kind);
    const letterhead = current.letterheads.find((l) => l.id === letterheadId);
    // Nothing to build yet (or the answer was deleted while queued); the status
    // chip already says which piece is missing.
    if (!body?.url || !letterhead) return;
    const base64 = await buildMergedDocument({ letterheadUrl: letterhead.url, bodyUrl: body.url });
    const url = await saveFile(mergedPath(kind), base64);
    if (!url) throw new Error("No fue posible guardar el documento generado.");
    await persist((d) => (bodyOf(d, kind)
      ? withMerged(d, kind, { url, letterheadId, builtAt: new Date().toISOString() })
      : d));
  };

  // Rebuilds run one at a time: each opens a hidden editor, and two of them
  // persisting at once would race on the same config doc.
  const rebuild = (kinds) => {
    const list = Array.from(new Set(kinds));
    if (!list.length) return queue.current;
    setBuilding((b) => ({ ...b, ...Object.fromEntries(list.map((k) => [k, true])) }));
    queue.current = queue.current.then(async () => {
      for (const kind of list) {
        try {
          await buildOne(kind);
          setBuildErrors((e) => ({ ...e, [kind]: null }));
        } catch (err) {
          setBuildErrors((e) => ({ ...e, [kind]: err?.message || "No fue posible generar el documento." }));
        } finally {
          setBuilding((b) => ({ ...b, [kind]: false }));
        }
      }
    });
    return queue.current;
  };

  // --- Membretes -----------------------------------------------------------

  const uploadLetterhead = (existingId) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = checkDocx(file);
    if (problem) return fail(problem);
    const id = existingId || newId();
    setBusy(existingId || "new");
    try {
      const url = await saveFile(letterheadPath(id), await readAsBase64(file));
      if (!url) throw new Error("sin URL");
      const now = new Date().toISOString();
      const next = await persist((d) => ({
        ...d,
        letterheads: existingId
          ? d.letterheads.map((l) => (l.id === id ? { ...l, name: file.name, url, updatedAt: now } : l))
          : [...d.letterheads, { id, name: file.name, url, updatedAt: now }],
        defaultLetterheadId: d.defaultLetterheadId || id,
      }));
      onFeedback({
        severity: "success",
        msg: existingId ? "Membrete reemplazado. Regenerando los documentos que lo usan." : "Membrete cargado.",
      });
      rebuild(kindsUsingLetterhead(next, id));
    } catch (err) {
      fail(err?.message === "sin URL" ? "No fue posible subir el membrete." : err?.message || "No fue posible subir el membrete.");
    } finally {
      setBusy(null);
    }
  };

  const makeDefault = async (id) => {
    try {
      const next = await persist((d) => ({ ...d, defaultLetterheadId: id }));
      rebuild(kindsUsingLetterhead(next, id));
    } catch (err) {
      fail(err.message);
    }
  };

  const removeLetterhead = async (letterhead) => {
    const current = docsRef.current;
    if (current.letterheads.length === 1) {
      return fail("Es el único membrete. Suba otro antes de eliminar este.");
    }
    const affected = kindsUsingLetterhead(current, letterhead.id);
    const names = affected.map((key) => labelOf(current, key));
    const question = names.length
      ? `¿Eliminar el membrete "${letterhead.name}"? Estos documentos pasarán al membrete predeterminado: ${names.join(", ")}.`
      : `¿Eliminar el membrete "${letterhead.name}"?`;
    if (!window.confirm(question)) return;
    try {
      await persist((d) => {
        const letterheads = d.letterheads.filter((l) => l.id !== letterhead.id);
        const unassigned = allDocKeys(d).reduce(
          (acc, key) => (bodyOf(acc, key).letterheadId === letterhead.id ? withBody(acc, key, { letterheadId: "" }) : acc),
          d
        );
        return {
          ...unassigned,
          letterheads,
          defaultLetterheadId: d.defaultLetterheadId === letterhead.id ? letterheads[0].id : d.defaultLetterheadId,
        };
      });
      rebuild(affected);
    } catch (err) {
      fail(err.message);
    }
  };

  // --- Textos --------------------------------------------------------------

  const assignLetterhead = async (kind, letterheadId) => {
    try {
      await persist((d) => withBody(d, kind, { letterheadId }));
      rebuild([kind]);
    } catch (err) {
      fail(err.message);
    }
  };

  const markBodySaved = (kind, url) =>
    persist((d) => (bodyOf(d, kind) ? withBody(d, kind, { url, updatedAt: new Date().toISOString() }) : d));

  const importBody = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = checkDocx(file);
    if (problem) return fail(problem);
    setBusy(`body:${kind}`);
    try {
      const url = await saveFile(bodyPath(kind), await readAsBase64(file));
      if (!url) throw new Error("No fue posible importar el texto.");
      await markBodySaved(kind, url);
      rebuild([kind]);
    } catch (err) {
      fail(err?.message || "No fue posible importar el texto.");
    } finally {
      setBusy(null);
    }
  };

  // Stores the shipped starting text as the document's text and rebuilds it.
  const adoptDefaultText = async (kind) => {
    const response = await fetch(defaultTextFor(kind), { cache: "no-store" });
    if (!response.ok) throw new Error("No fue posible cargar el texto predeterminado.");
    const url = await saveFile(bodyPath(kind), await readAsBase64(await response.blob()));
    if (!url) throw new Error("No fue posible guardar el texto predeterminado.");
    await markBodySaved(kind, url);
    rebuild([kind]);
  };

  const restoreDefaultText = async (kind) => {
    if (bodyOf(docsRef.current, kind)?.url
      && !window.confirm("¿Reemplazar el texto actual por el texto predeterminado? Se perderán los cambios hechos al texto.")) {
      return;
    }
    setBusy(`body:${kind}`);
    try {
      await adoptDefaultText(kind);
    } catch (err) {
      fail(err?.message || "No fue posible usar el texto predeterminado.");
    } finally {
      setBusy(null);
    }
  };

  const openEditor = (kind) => {
    editorSaved.current = false;
    setEditorKind(kind);
  };

  // The editor autosaves over the same path while typing. The first save of a
  // new text is recorded right away (so its URL is never lost); the rebuild
  // waits for the dialog to close instead of running on every keystroke.
  const handleEditorSaved = async (kind, url) => {
    const firstSave = !editorSaved.current;
    editorSaved.current = true;
    if (firstSave && url !== bodyOf(docsRef.current, kind)?.url) {
      try {
        await markBodySaved(kind, url);
      } catch (err) {
        fail(err.message);
      }
    }
  };

  const closeEditor = async () => {
    const kind = editorKind;
    setEditorKind(null);
    if (!kind) return;
    // A document with no text yet opens on the starting text; closing it without
    // edits accepts that text as-is.
    if (!editorSaved.current) {
      if (bodyOf(docsRef.current, kind)?.url) return;
      try {
        await adoptDefaultText(kind);
      } catch (err) {
        fail(err.message);
      }
      return;
    }
    try {
      await markBodySaved(kind, bodyOf(docsRef.current, kind)?.url);
      rebuild([kind]);
    } catch (err) {
      fail(err.message);
    }
  };

  // --- Plantillas de respuesta ----------------------------------------------

  const [newAnswerName, setNewAnswerName] = useState("");
  const [adding, setAdding] = useState(false);
  // Name drafts per answer, saved on blur so each keystroke is not a write.
  const [nameDrafts, setNameDrafts] = useState({});

  const addAnswer = async () => {
    const name = newAnswerName.trim().slice(0, 160);
    if (!name) return fail("Escriba un nombre para la plantilla.");
    if (docsRef.current.answers.length >= MAX_ANSWER_TEMPLATES) {
      return fail(`Se admiten máximo ${MAX_ANSWER_TEMPLATES} plantillas de respuesta.`);
    }
    if (docsRef.current.answers.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
      return fail("Ya existe una plantilla con ese nombre.");
    }
    const id = newId();
    const key = answerKey(id);
    setBusy(`body:${key}`);
    setAdding(true);
    try {
      await persist((d) => ({
        ...d,
        answers: [
          ...d.answers,
          { id, name, active: true, letterheadId: "", body: { url: "", updatedAt: "" }, merged: { url: "", letterheadId: "", builtAt: "" } },
        ],
      }));
      setNewAnswerName("");
      // A new template is usable right away: it starts on the shipped letter.
      await adoptDefaultText(key);
    } catch (err) {
      fail(err?.message || "No fue posible crear la plantilla.");
    } finally {
      setBusy(null);
      setAdding(false);
    }
  };

  const renameAnswer = async (answer) => {
    const draft = nameDrafts[answer.id];
    if (draft === undefined) return;
    const name = draft.trim().slice(0, 160);
    setNameDrafts(({ [answer.id]: _, ...rest }) => rest);
    if (!name || name === answer.name) return;
    if (docsRef.current.answers.some((a) => a.id !== answer.id && a.name.toLowerCase() === name.toLowerCase())) {
      return fail("Ya existe una plantilla con ese nombre.");
    }
    try {
      await persist((d) => ({ ...d, answers: d.answers.map((a) => (a.id === answer.id ? { ...a, name } : a)) }));
    } catch (err) {
      fail(err.message);
    }
  };

  const setAnswerActive = async (answer, active) => {
    try {
      await persist((d) => ({ ...d, answers: d.answers.map((a) => (a.id === answer.id ? { ...a, active } : a)) }));
    } catch (err) {
      fail(err.message);
    }
  };

  // Answers already in progress keep their own copy of the document, so
  // deleting a template never touches a flow that was started from it.
  const removeAnswer = async (answer) => {
    if (!window.confirm(`¿Eliminar la plantilla de respuesta "${answer.name}"? Las respuestas ya iniciadas con ella no se afectan.`)) return;
    try {
      await persist((d) => ({ ...d, answers: d.answers.filter((a) => a.id !== answer.id) }));
    } catch (err) {
      fail(err.message);
    }
  };

  const labelOf = (current, key) =>
    findAnswer(current, key)?.name || pqrsdText(DOCUMENT_KINDS.find((k) => k.key === key)?.label) || key;

  const editorMeta = editorKind
    ? isAnswerKey(editorKind)
      ? { key: editorKind, label: `Plantilla de respuesta: ${labelOf(docs, editorKind)}`, tags: ANSWER_TAGS }
      : DOCUMENT_KINDS.find((k) => k.key === editorKind)
    : null;
  const defaultLetterhead = docs.letterheads.find((l) => l.id === docs.defaultLetterheadId);
  const anyBuilding = Object.values(building).some(Boolean);

  // The controls every built document has: its membrete, its text and the
  // generated .docx.
  const renderDocControls = (key) => {
    const status = building[key] ? null : documentStatus(docs, key);
    const merged = mergedOf(docs, key);
    const body = bodyOf(docs, key);
    return (
      <>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
          {building[key] ? (
            <Chip size="small" icon={<CircularProgress size={12} />} label="Generando…" />
          ) : (
            <Chip size="small" color={STATUS[status].color} label={STATUS[status].label} />
          )}
          <TextField
            select
            size="small"
            label="Membrete"
            value={body.letterheadId}
            // "" is the default membrete, a real choice: without these MUI
            // renders it as an empty field with a floating label.
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => assignLetterhead(key, e.target.value)}
            disabled={!docs.letterheads.length || !!building[key]}
            sx={{ minWidth: 260 }}
          >
            <MenuItem value="">
              Predeterminado{defaultLetterhead ? ` (${defaultLetterhead.name || "Membrete"})` : ""}
            </MenuItem>
            {docs.letterheads.map((l) => (
              <MenuItem key={l.id} value={l.id}>{l.name || "Membrete"}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={() => openEditor(key)} disabled={!!busy}>
            Editar texto
          </Button>
          <Button
            size="small"
            startIcon={busy === `body:${key}` ? <CircularProgress size={14} /> : <RestoreIcon />}
            onClick={() => restoreDefaultText(key)}
            disabled={!!busy || !!building[key]}
          >
            {body.url ? "Restaurar predeterminado" : "Usar texto predeterminado"}
          </Button>
          <Button
            size="small"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={!!busy}
          >
            Importar .docx
            <input hidden type="file" accept=".docx" onChange={importBody(key)} />
          </Button>
          {merged.url && (
            <Chip
              icon={<DescriptionIcon />}
              label="Documento generado"
              component="a"
              href={merged.url}
              target="_blank"
              rel="noopener"
              clickable
              variant="outlined"
              size="small"
            />
          )}
          {status === "stale" && (
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => rebuild([key])}>
              Regenerar
            </Button>
          )}
        </Stack>

        {buildErrors[key] && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setBuildErrors((e) => ({ ...e, [key]: null }))}>
            {buildErrors[key]}
          </Alert>
        )}
      </>
    );
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Membretes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Un membrete es un documento Word con el encabezado y el pie de página de la entidad y el
            cuerpo vacío. Todos los documentos usan el <strong>predeterminado</strong>, salvo los que
            elijan otro abajo. Al reemplazar un membrete, los documentos que lo usan se regeneran solos.
          </Typography>

          {docs.letterheads.length === 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              Aún no hay membretes. Suba uno para poder generar los documentos.
            </Typography>
          )}

          <Stack spacing={1} sx={{ mb: 2 }}>
            {docs.letterheads.map((l) => {
              const isDefault = l.id === docs.defaultLetterheadId;
              return (
                <Stack
                  key={l.id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
                >
                  <Chip
                    icon={<DescriptionIcon />}
                    label={l.name || "Membrete"}
                    component="a"
                    href={l.url}
                    target="_blank"
                    rel="noopener"
                    clickable
                    variant="outlined"
                    size="small"
                  />
                  {isDefault ? (
                    <Chip label="Predeterminado" color="primary" size="small" />
                  ) : (
                    <Button size="small" onClick={() => makeDefault(l.id)}>Usar como predeterminado</Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    component="label"
                    startIcon={busy === l.id ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    disabled={!!busy}
                  >
                    Reemplazar
                    <input hidden type="file" accept=".docx" onChange={uploadLetterhead(l.id)} />
                  </Button>
                  <Tooltip title="Eliminar">
                    <span>
                      <IconButton size="small" onClick={() => removeLetterhead(l)} disabled={!!busy}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>

          <Button
            variant="outlined"
            size="small"
            component="label"
            startIcon={busy === "new" ? <CircularProgress size={14} /> : <UploadFileIcon />}
            disabled={!!busy}
          >
            {busy === "new" ? "Subiendo…" : "Subir membrete .docx"}
            <input hidden type="file" accept=".docx" onChange={uploadLetterhead(null)} />
          </Button>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              El membrete debe traer, en el <strong>encabezado de Word</strong>, una tabla de una fila y
              dos columnas vacía: ahí es donde el sistema escribe la fecha, el número de radicado y el
              código QR, y luego le quita los bordes. Un membrete sin esa tabla genera el PDF sin el
              sello del radicado.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6">Documentos</Typography>
            <Button
              size="small"
              startIcon={anyBuilding ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={() => rebuild(allDocKeys(docs))}
              disabled={anyBuilding || !docs.letterheads.length}
            >
              Regenerar todos
            </Button>
          </Stack>
          {anyBuilding && (
            // The merge runs in this tab and pauses while it is not visible.
            <Alert severity="info" sx={{ mb: 2 }}>
              Generando documentos. Mantenga esta pestaña abierta y visible hasta que terminen.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escriba solo el texto de cada documento, con las etiquetas que el sistema reemplaza al
            radicar. Al cerrar el editor, el texto se inserta en el cuerpo de su membrete y se guarda
            el documento que el sistema usa.
          </Typography>

          {DOCUMENT_KINDS.map(({ key, label, hint, whenMissing }, idx) => (
            <Box key={key}>
              {idx > 0 && <Divider sx={{ my: 2 }} />}
              <Typography variant="subtitle2">{pqrsdText(label)}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {pqrsdText(hint)}
              </Typography>
              {renderDocControls(key)}
              {!docs.merged[key].url && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                  {pqrsdText(whenMissing)}
                </Typography>
              )}
            </Box>
          ))}
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Plantillas de respuesta</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pqrsdText("Son las plantillas que se ofrecen al iniciar la respuesta de una PQRSD. Al iniciarla, el sistema llena las etiquetas con los datos de la PQRSD y del ciudadano; quien elabora escribe la respuesta sobre esa copia y la cadena de aprobación se elige en ese momento. Solo se ofrecen las plantillas activas que ya tienen documento generado.")}
          </Typography>

          {docs.answers.length === 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              {pqrsdText("Aún no hay plantillas de respuesta. Sin al menos una activa no es posible responder una PQRSD.")}
            </Typography>
          )}

          <Stack spacing={2} divider={<Divider flexItem />} sx={{ mb: 2 }}>
            {docs.answers.map((answer) => {
              const key = answerKey(answer.id);
              return (
                <Box key={answer.id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      label="Nombre"
                      value={nameDrafts[answer.id] ?? answer.name}
                      onChange={(e) => setNameDrafts((n) => ({ ...n, [answer.id]: e.target.value }))}
                      onBlur={() => renameAnswer(answer)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      inputProps={{ maxLength: 160 }}
                      sx={{ flex: 1, maxWidth: 420 }}
                    />
                    <Tooltip title={answer.active ? "Se ofrece al iniciar una respuesta" : "No se ofrece al iniciar una respuesta"}>
                      <Stack direction="row" alignItems="center">
                        <Switch
                          size="small"
                          checked={answer.active}
                          onChange={(e) => setAnswerActive(answer, e.target.checked)}
                        />
                        <Typography variant="body2">{answer.active ? "Activa" : "Inactiva"}</Typography>
                      </Stack>
                    </Tooltip>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Eliminar plantilla">
                      <span>
                        <IconButton size="small" onClick={() => removeAnswer(answer)} disabled={!!busy || !!building[key]}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  {renderDocControls(key)}
                </Box>
              );
            })}
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              label="Nueva plantilla"
              placeholder="Ej.: Respuesta de fondo, Traslado por competencia"
              value={newAnswerName}
              onChange={(e) => setNewAnswerName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addAnswer(); }}
              inputProps={{ maxLength: 160 }}
              sx={{ minWidth: 320 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={adding ? <CircularProgress size={14} /> : <AddIcon />}
              onClick={addAnswer}
              disabled={!!busy || !newAnswerName.trim()}
            >
              Agregar plantilla
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {editorMeta && (
        <PqrsdTemplateDialog
          open
          kind={editorMeta.key}
          label={editorMeta.label}
          tags={editorMeta.tags}
          file={{
            name: `pqrsd_body_${editorMeta.key}`,
            path: bodyPath(editorMeta.key),
            // No text yet: start from the shipped default instead of a blank page.
            url: bodyOf(docs, editorMeta.key)?.url || defaultTextFor(editorMeta.key),
          }}
          onClose={closeEditor}
          onSaved={handleEditorSaved}
        />
      )}
    </>
  );
}
